/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import mbpLogger from '@module-logger/mbp';
import Slider from 'react-slick';
import { useUIDSeed } from 'react-uid';
import {
    bool, func, object, string,
} from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { Button, useMediaQuery } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { withStyles } from '@material-ui/styles';
import ResponsiveImage from '../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import getPrice from '../../../../helpers/getPrice/getPrice';

const styles = (theme) => ({
    productContainer: {
        display: 'flex !important',
        flexDirection: 'column',
        border: '5px solid transparent',
        '&:hover': {
            border: '5px solid #cca2cf',
        },
    },
    selected: {
        border: '5px solid #cca2cf',
    },
    imageContainer: {
        height: '300px',
    },
    images: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    productDetails: {
        backgroundColor: 'rgb(0,0,0,5%)',
        flexDirection: 'column',
        alignItems: 'center',
        flex: '1 1 auto',
        padding: '40px',
        '@media screen and (max-width: 768px)': {
            padding: '40px 10px',
        },
        '& .MuiButton-root': {
            color: theme.palette.white,
            textTransform: 'uppercase',
            fontSize: '13px',
            marginTop: '20px',
            padding: '5px 20px',
            fontWeight: '700',
            width: 'fit-content',
            backgroundColor: theme.palette.colorPrimary,
            '-webkit-font-smoothing': 'antialiased',
            '&:hover': {
                backgroundColor: theme.palette.colorPrimary,
            },
        },
        '& #product_name': {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            flex: '1 1 auto',
            fontFamily: 'ivypresto-text,PlayfairDisplayBold, serif',
            fontSize: '1.4em',
            paddingBottom: '10px',
        },
        '& #product_description': {
            fontSize: '0.8em',
            paddingBottom: '30px',
        },
        '& #product_price': {
            fontSize: '0.8em',
        },
    },
    sliderContainer: {
        width: '100%',
        '& a': {
            display: 'inherit',
            fontSize: '16px',
            fontWeight: '400',
            textDecoration: 'none !important',
            color: theme.palette.cms?.black || ' #000',
            outline: '0 !important',
        },
        position: 'relative',
        '& .slick-list': {
            overflow: 'hidden',
            margin: '0 auto',
            width: '100%',
        },
        '& .slick-slide': {
            display: 'flex',
            height: 'inherit !important',
            padding: '0 8px',
            '@media screen and (max-width: 768px)': {
                padding: '0',
            },
            verticalAlign: 'top',
            '& div': {
                display: 'flex',
                justifyContent: 'center',
            },
            '&:active': {
                '@media screen and (max-width: 1100px)': {
                    cursor: 'grabbing',
                },
            },
        },
        '& .slick-prev, & .slick-next': {
            cursor: 'pointer',
            bottom: '-56px',
            width: 40,
            height: 40,
            position: 'absolute',
            alignItems: 'center !important',
            top: '50px',
            border: `1px solid ${theme.palette.black}`,
            borderRadius: '25px',
        },
        '& .slick-prev': {
            top: '43%',
            left: '-46px',
        },
        '& .slick-next': {
            top: '43%',
            right: '-50px',
        },
        '& .slick-disabled': {
            '& .back': {
                filter: 'invert(70%)',
            },
            '& .forward': {
                filter: 'invert(70%)',
            },
            cursor: 'not-allowed',
            borderColor: `${theme.palette.cms?.horizentalLineBorder || '#DEDEDE'} !important`,
            backgroundColor: '#00000008 !important',
            color: theme.palette.cms?.diabledBorderColor || '#DCDCDC',
        },
        '& .slick-dots': {
            display: 'flex !important',
            justifyContent: 'space-between',
            padding: '0',
            listStyle: 'none',
            backgroundColor: 'rgba(0,0,0,0.2)',
        },
        '& .slick-dots li': {
            width: '6px',
            height: '6px',
            margin: '0 3px',
            borderRadius: '999px',
            transition: 'all 0.5s ease-in-out',
            willChange: 'width',
            backgroundColor: 'transparent',
        },
        '& .slick-dots li.slick-active': {
            backgroundColor: 'rgba(0,0,0,0.4)',
            width: '100px',
        },
        '& .slick-track': {
            display: 'flex !important',
            flex: '1 1 auto',
        },
    },
    skuLabelAmount: {
        color: theme.cms?.skuLabelAmountTextColor || '#585858',
        marginLeft: '10px',
        fontWeight: '500',
        [theme.breakpoints.down('600')]: {
            fontSize: '15px',
            textAlign: 'right',
            '&--retail, &--sale': {
                fontSize: '15px',
            },
        },
        '&--retail': {
            marginLeft: '5px',
            textDecoration: 'line-through',
            color: '#ed0000',
        },
        '&--sale': {
            marginLeft: '5px',
            color: '#2e8540',
        },
    },
});

const MobileSlider = ({
    classes, product, selectVariant, dynamicPriceList, selectedVariant, displayDescription, trackEvent, variantsDetail,
}) => {
    const { name, shortDescription } = product;
    const seed = useUIDSeed();
    const isTab = useMediaQuery('(max-width: 1100px)');

    const getVariant = (price) => {
        const min = Math.min(...product.productSkus.map((variant) => variant.prices[0].value));
        const max = Math.max(...product.productSkus.map((variant) => variant.prices[0].value));
        let variant = 'Basic';
        if ((min < price) && (price < max)) variant = 'Deluxe';
        if (price === max) variant = 'Premium';
        return variant;
    };

    const choosePlan = (item, variant, currentSkuActualPriceDetails, currentSkuRetailPrice) => {
        selectVariant(item, variant, currentSkuActualPriceDetails?.hasSale ? currentSkuActualPriceDetails?.value : currentSkuRetailPrice);
        trackEvent('Subscriptions Experience', 'click – Step 1', `Choose this plan - ${name} ${variant}`);
    };

    const renderSlider = (item) => {
        const variantDetail = product?.productSkus?.find((x) => item.variant_sku === x.id);
        const { image, prices } = variantDetail;
        const discountPrices = dynamicPriceList(variantDetail);
        const currentSkuRetailPrice = Number((discountPrices.length > 0 ? discountPrices : prices)?.find((price) => price.type === 'retail')?.value || 0);
        const currentSkuActualPriceDetails = getPrice((discountPrices.length > 0 ? discountPrices : prices));

        if (currentSkuActualPriceDetails?.hasSale && currentSkuRetailPrice === currentSkuActualPriceDetails?.value) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                component: 'Subscription Exp Slider.js',
                message: `Error with subscription prices on slider when customer selects subscription option, Display price ${currentSkuRetailPrice} is same as Sale Price ${currentSkuActualPriceDetails?.value} }`,
            });
        }

        const responsiveImagePath = () => {
            const extension = (image?.name || '').includes('_gif') ? '.gif' : 'x.jpg';
            return `${image?.path + image?.name + extension}`;
        };

        const imagePath = item?.variant_image?.url ? item.variant_image.url : responsiveImagePath();
        const variant = item?.variant_name ? item.variant_name : getVariant(prices?.[0]?.value);
        const description = item?.variant_description ? item.variant_description : shortDescription;

        return (
            <div key={seed(variantDetail)} className={`${classes.productContainer} ${selectedVariant === variant && classes.selected}`}>
                <div className={classes.imageContainer}>
                    <ResponsiveImage
                        alt={image?.name}
                        fullWidth
                        className={classes.images}
                        path={imagePath}
                        params={{ desktop: 'quality=100', mobile: 'quality=100' }}
                    />
                </div>
                <div className={classes.productDetails}>
                    <div id="product_name">{variant}</div>
                    {displayDescription && <div id="product_description">{ReactHtmlParser(description)}</div>}
                    <div id="product_price">Price: &nbsp;
                        <span className={`${classes.skuLabelAmount}${currentSkuActualPriceDetails?.hasSale ? '--retail' : ''}`}>${currentSkuRetailPrice}</span>
                        {currentSkuActualPriceDetails?.hasSale && <span className={`${classes.skuLabelAmount}--sale`}>${currentSkuActualPriceDetails?.value}</span>}
                    </div>
                    <Button data-testid={`choose-this-plan-${variant}`} onClick={() => choosePlan(variantDetail, variant, currentSkuActualPriceDetails, currentSkuRetailPrice)}>CHOOSE THIS PLAN</Button>
                </div>
            </div>
        );
    };

    return (
        <Slider
            infinite
            speed={500}
            slidesToShow={3}
            slidesToScroll={1}
            arrows={!isTab}
            dots={isTab}
            customPaging={() => <div />}
            nextArrow={<ArrowForwardIcon />}
            prevArrow={<ArrowBackIcon />}
            className={`carousel-container ${classes.sliderContainer}`}
            responsive={[
                {
                    breakpoint: 1100, // tablet breakpoint
                    settings: {
                        slidesToShow: 2,
                        slidesToScroll: 2,
                    },
                },
                {
                    breakpoint: 560, // mobile breakpoint
                    settings: {
                        slidesToShow: 1,
                        slidesToScroll: 1,
                    },
                },
            ]}
        >{variantsDetail?.map((x) => renderSlider(x))}
        </Slider>
    );
};

MobileSlider.propTypes = {
    product: object.isRequired,
    classes: object.isRequired,
    selectVariant: func.isRequired,
    dynamicPriceList: func.isRequired,
    selectedVariant: string.isRequired,
    displayDescription: bool.isRequired,
    trackEvent: func.isRequired,
    variantsDetail: object.isRequired,
};

export default  withStyles(styles)(MobileSlider);
